import * as Y from 'yjs';
import { defaultChart } from './chart';
import { convertProperty } from './convert';
import type { Layout, PropertyDef, PropertyType, RowData, SelectOption, ViewDef } from './db-types';
import type { ValueContext } from './db-values';
import { newId } from './ids';
import type { RepeatRule } from './repeat';
import type { PageParent } from './types';
import type { Workspace } from './workspace';
import { t } from '@lumen/platform';

export const LAYOUT_LABELS: Record<Layout, string> = { table: t('Table'), board: t('Board'), list: t('List'), gallery: t('Gallery'), calendar: t('Calendar'), timeline: t('Timeline'), chart: t('Chart') };

export const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  title: t('Title'),
  text: t('Text'),
  number: t('Number'),
  select: t('Select'),
  multi_select: t('Multi-select'),
  status: t('Status'),
  date: t('Date'),
  person: t('Person'),
  files: t('Files & media'),
  checkbox: t('Checkbox'),
  url: t('URL'),
  email: t('Email'),
  phone: t('Phone'),
  formula: t('Formula'),
  relation: t('Relation'),
  rollup: t('Rollup'),
  created_time: t('Created time'),
  created_by: t('Created by'),
  last_edited_time: t('Last edited time'),
  last_edited_by: t('Last edited by'),
  unique_id: t('ID'),
  button: t('Button'),
};

export const TITLE_PROP = 'title';

export function defaultStatusOptions(): SelectOption[] {
  return [
    { id: 'not-started', name: t('Not started'), color: 'default', group: 'todo' },
    { id: 'in-progress', name: t('In progress'), color: 'blue', group: 'in_progress' },
    { id: 'done', name: t('Done'), color: 'green', group: 'complete' },
  ];
}

export const STATUS_GROUPS: { id: 'todo' | 'in_progress' | 'complete'; label: string }[] = [
  { id: 'todo', label: t('To-do') },
  { id: 'in_progress', label: t('In progress') },
  { id: 'complete', label: t('Complete') },
];

export function newView(layout: Layout, props: PropertyDef[], name?: string): ViewDef {
  const statusOrSelect = props.find((p) => p.type === 'status') ?? props.find((p) => p.type === 'select') ?? props.find((p) => p.type === 'multi_select') ?? props.find((p) => p.type === 'person');
  const dateProp = props.find((p) => p.type === 'date');
  return {
    id: newId(),
    name: name ?? LAYOUT_LABELS[layout],
    layout,
    filter: null,
    sorts: [],
    groupBy: layout === 'board' && statusOrSelect ? { propId: statusOrSelect.id } : null,
    visible: props.filter((p) => p.type !== 'title').map((p) => p.id),
    widths: {},
    calcs: {},
    loadLimit: layout === 'table' || layout === 'list' ? 50 : 25,
    openIn: 'side',
    card: { preview: layout === 'gallery' ? 'content' : 'none', size: 'medium', fitImage: false },
    calendarBy: dateProp?.id ?? null,
    showWeekends: true,
    timeline: { startProp: dateProp?.id ?? null, endProp: dateProp?.id ?? null, zoom: 'month', showTable: true },
    ...(layout === 'chart' ? { chart: defaultChart(props) } : {}),
  };
}

type YDb = Y.Map<unknown>;

/**
 * Database schema, views, rows and templates, stored in the workspace document (ADR 0005).
 * A view set is a list of views over a source database: the database's own (id = database id)
 * or a linked view block's.
 */
export class DatabaseModel {
  readonly dbs: Y.Map<YDb>;
  readonly viewSets: Y.Map<YDb>;

  constructor(readonly ws: Workspace) {
    this.dbs = ws.doc.getMap('databases');
    this.viewSets = ws.doc.getMap('viewSets');
  }

  private transact<T>(fn: () => T): T {
    let out!: T;
    this.ws.doc.transact(() => {
      out = fn();
    });
    return out;
  }

  private db(id: string): YDb | undefined {
    return this.dbs.get(id);
  }

  isDatabase(id: string): boolean {
    return this.dbs.has(id);
  }

  // ───────────── creation ─────────────

  createDatabase(opts: { parent: PageParent; title?: string; inline?: boolean; layout?: Layout; index?: number; id?: string; withRows?: boolean }): string {
    return this.transact(() => {
      const id = this.ws.createPage({ parent: opts.parent, title: opts.title ?? '', kind: 'database', inline: opts.inline ?? false, index: opts.index, id: opts.id });
      this.initDatabase(id, opts.layout ?? 'table', opts.withRows !== false);
      return id;
    });
  }

  /** Turns an existing (empty) page into a full-page database (quick start "Table" / "Board"). */
  convertToDatabase(pageId: string, layout: Layout = 'table'): void {
    const y = this.ws.pages.get(pageId);
    if (!y || this.dbs.has(pageId)) return;
    this.transact(() => {
      y.set('kind', 'database');
      this.initDatabase(pageId, layout, true);
    });
  }

  private initDatabase(id: string, layout: Layout, withRows: boolean): void {
    const y = new Y.Map<unknown>();
    const props = new Y.Map<PropertyDef>();
    const order = new Y.Array<string>();
    y.set('properties', props);
    y.set('propertyOrder', order);
    y.set('locked', false);
    y.set('nextUniqueId', 1);
    y.set('templates', new Y.Array<string>());
    y.set('repeat', new Y.Map<unknown>());
    this.dbs.set(id, y);
    const defs: PropertyDef[] = [{ id: TITLE_PROP, name: t('Name'), type: 'title' }];
    if (layout === 'board') defs.push({ id: newId(), name: t('Status'), type: 'status', options: defaultStatusOptions() });
    else if (layout === 'calendar') defs.push({ id: newId(), name: t('Date'), type: 'date' });
    else if (layout === 'timeline') defs.push({ id: newId(), name: t('Dates'), type: 'date' });
    else defs.push({ id: newId(), name: t('Tags'), type: 'multi_select', options: [] });
    for (const d of defs) {
      props.set(d.id, d);
      order.push([d.id]);
    }
    this.ensureViewSet(id, id);
    this.addView(id, layout);
    if (withRows) {
      if (layout === 'board') for (const n of [1, 2, 3]) this.addRow(id, { title: `Card ${n}` });
      else if (layout === 'table' || layout === 'list' || layout === 'gallery') for (let i = 0; i < 3; i++) this.addRow(id, {});
    }
  }

  private ensureViewSet(viewSetId: string, sourceDbId: string): YDb {
    let vs = this.viewSets.get(viewSetId);
    if (!vs) {
      vs = new Y.Map<unknown>();
      vs.set('source', sourceDbId);
      vs.set('views', new Y.Array<string>());
      vs.set('viewDefs', new Y.Map<ViewDef>());
      this.viewSets.set(viewSetId, vs);
    }
    return vs;
  }

  /** A linked view of `sourceDbId` with its own views (P-354). */
  createLinkedViewSet(sourceDbId: string, layout: Layout = 'table'): string {
    const id = newId();
    this.transact(() => {
      this.ensureViewSet(id, sourceDbId);
      this.addView(id, layout);
    });
    return id;
  }

  sourceOf(viewSetId: string): string | null {
    return (this.viewSets.get(viewSetId)?.get('source') as string | undefined) ?? (this.dbs.has(viewSetId) ? viewSetId : null);
  }

  // ───────────── schema ─────────────

  schema(dbId: string): PropertyDef[] {
    const y = this.db(dbId);
    if (!y) return [];
    const props = y.get('properties') as Y.Map<PropertyDef>;
    const order = (y.get('propertyOrder') as Y.Array<string>).toArray();
    const seen = new Set<string>();
    const out: PropertyDef[] = [];
    for (const id of order) {
      const p = props.get(id);
      if (p && !seen.has(id)) {
        seen.add(id);
        out.push(p);
      }
    }
    for (const [id, p] of props.entries()) if (!seen.has(id)) out.push(p);
    return out.sort((a, b) => (a.type === 'title' ? -1 : b.type === 'title' ? 1 : 0));
  }

  property(dbId: string, propId: string): PropertyDef | null {
    return ((this.db(dbId)?.get('properties') as Y.Map<PropertyDef> | undefined)?.get(propId) as PropertyDef | undefined) ?? null;
  }

  /** "Show in sidebar": list the database's row pages under it in the sidebar (off by default). */
  showsInSidebar(dbId: string): boolean {
    return !!this.db(dbId)?.get('showInSidebar');
  }

  setShowInSidebar(dbId: string, on: boolean): void {
    this.db(dbId)?.set('showInSidebar', on);
  }

  isLocked(dbId: string): boolean {
    return !!this.db(dbId)?.get('locked');
  }

  setLocked(dbId: string, locked: boolean): void {
    this.db(dbId)?.set('locked', locked);
  }

  addProperty(dbId: string, def: Omit<PropertyDef, 'id'> & { id?: string }, index?: number): string {
    const y = this.db(dbId);
    if (!y) throw new Error(`database ${dbId} not found`);
    const id = def.id ?? newId();
    this.transact(() => {
      const p: PropertyDef = { ...def, id } as PropertyDef;
      if (p.type === 'status' && !p.options) p.options = defaultStatusOptions();
      if ((p.type === 'select' || p.type === 'multi_select') && !p.options) p.options = [];
      (y.get('properties') as Y.Map<PropertyDef>).set(id, p);
      const order = y.get('propertyOrder') as Y.Array<string>;
      order.insert(index === undefined ? order.length : Math.max(0, Math.min(index, order.length)), [id]);
      if (p.type === 'unique_id') this.assignUniqueIds(dbId, id);
      if (p.type === 'relation' && p.relation?.twoWay && !p.relation.syncedPropId) {
        const back = newId();
        const self = this.ws.getPage(dbId)?.title || t('Untitled');
        (y.get('properties') as Y.Map<PropertyDef>).set(id, { ...p, relation: { ...p.relation, syncedPropId: back } });
        this.addProperty(p.relation.dbId, { id: back, name: `Related to ${self} (${p.name})`, type: 'relation', relation: { dbId, twoWay: true, syncedPropId: id, limit: 'many' } });
      }
      // New properties show in every view.
      for (const vsId of this.viewSetIdsOf(dbId)) for (const v of this.views(vsId)) if (!v.visible.includes(id)) this.updateView(vsId, v.id, { visible: [...v.visible, id] });
    });
    return id;
  }

  private assignUniqueIds(dbId: string, propId: string) {
    const y = this.db(dbId)!;
    let next = (y.get('nextUniqueId') as number) ?? 1;
    for (const r of this.rows(dbId)) {
      if (typeof r.props[propId] !== 'number') this.ws.setProperty(r.id, propId, next++);
    }
    y.set('nextUniqueId', next);
  }

  /** Updates a property definition; a type change converts existing values (P-391). */
  updateProperty(dbId: string, propId: string, patch: Partial<PropertyDef>): void {
    const y = this.db(dbId);
    const cur = this.property(dbId, propId);
    if (!y || !cur) return;
    this.transact(() => {
      let next: PropertyDef = { ...cur, ...patch };
      if (patch.type && patch.type !== cur.type && cur.type !== 'title') {
        const rows = this.rows(dbId, true);
        const raw = Object.fromEntries(rows.map((r) => [r.id, cur.type === 'created_time' ? r.createdAt : cur.type === 'last_edited_time' ? r.editedAt : cur.type === 'created_by' ? r.createdBy : cur.type === 'last_edited_by' ? r.editedBy : r.props[propId]]));
        const res = convertProperty(cur, raw, patch.type, (id) => this.ws.getMember(id)?.name ?? id);
        if (res.options) next = { ...next, options: patch.type === 'status' && !res.options.length ? defaultStatusOptions() : res.options };
        for (const r of rows) this.ws.setProperty(r.id, propId, res.values[r.id] ?? null);
        if (patch.type === 'unique_id') {
          (y.get('properties') as Y.Map<PropertyDef>).set(propId, next);
          this.assignUniqueIds(dbId, propId);
        }
      }
      (y.get('properties') as Y.Map<PropertyDef>).set(propId, next);
    });
  }

  deleteProperty(dbId: string, propId: string): void {
    const y = this.db(dbId);
    if (!y || propId === TITLE_PROP) return;
    this.transact(() => {
      (y.get('properties') as Y.Map<PropertyDef>).delete(propId);
      const order = y.get('propertyOrder') as Y.Array<string>;
      const i = order.toArray().indexOf(propId);
      if (i >= 0) order.delete(i, 1);
      for (const vsId of this.viewSetIdsOf(dbId))
        for (const v of this.views(vsId)) {
          const patch: Partial<ViewDef> = {};
          if (v.visible.includes(propId)) patch.visible = v.visible.filter((x) => x !== propId);
          if (v.sorts.some((s) => s.propId === propId)) patch.sorts = v.sorts.filter((s) => s.propId !== propId);
          if (v.groupBy?.propId === propId) patch.groupBy = null;
          if (Object.keys(patch).length) this.updateView(vsId, v.id, patch);
        }
    });
  }

  duplicateProperty(dbId: string, propId: string): string | null {
    const p = this.property(dbId, propId);
    if (!p || p.type === 'title') return null;
    const order = this.schema(dbId).map((x) => x.id);
    const { id: _id, ...rest } = p;
    const copy = this.addProperty(dbId, { ...rest, name: `${p.name} (1)`, ...(p.relation ? { relation: { ...p.relation, twoWay: false, syncedPropId: undefined } } : {}) }, order.indexOf(propId) + 1);
    if (p.type !== 'unique_id') for (const r of this.rows(dbId, true)) if (r.props[propId] !== undefined) this.ws.setProperty(r.id, copy, structuredClone(r.props[propId]));
    return copy;
  }

  moveProperty(dbId: string, propId: string, index: number): void {
    const y = this.db(dbId);
    if (!y) return;
    this.transact(() => {
      const order = y.get('propertyOrder') as Y.Array<string>;
      const arr = order.toArray();
      const i = arr.indexOf(propId);
      if (i < 0) return;
      order.delete(i, 1);
      order.insert(Math.max(0, Math.min(index, order.length)), [propId]);
    });
  }

  /** Adds an option to a select-like property and returns its id. */
  addOption(dbId: string, propId: string, option: Omit<SelectOption, 'id'> & { id?: string }): string {
    const p = this.property(dbId, propId);
    const id = option.id ?? newId();
    if (!p) return id;
    this.updateProperty(dbId, propId, { options: [...(p.options ?? []), { ...option, id }] });
    return id;
  }

  // ───────────── rows ─────────────

  private rowData(id: string): RowData | null {
    const p = this.ws.getPage(id);
    if (!p) return null;
    return { id, title: p.title, props: p.properties, createdAt: p.createdAt, createdBy: p.createdBy, editedAt: p.editedAt, editedBy: p.editedBy };
  }

  /** Rows in manual order (not trashed; templates only when asked). */
  rows(dbId: string, includeTemplates = false): RowData[] {
    const page = this.ws.getPage(dbId);
    if (!page) return [];
    return page.childIds
      .map((id) => this.ws.getPage(id))
      .filter((p): p is NonNullable<typeof p> => !!p && !p.trashed && (includeTemplates || !p.isTemplate))
      .map((p) => this.rowData(p.id)!);
  }

  row(id: string): RowData | null {
    return this.rowData(id);
  }

  dbOfRow(rowId: string): string | null {
    const p = this.ws.getPage(rowId);
    return p?.parent.type === 'database' ? p.parent.id : null;
  }

  addRow(dbId: string, opts: { title?: string; values?: Record<string, unknown>; index?: number; isTemplate?: boolean; id?: string }): string {
    const y = this.db(dbId);
    return this.transact(() => {
      const id = this.ws.createPage({ parent: { type: 'database', id: dbId }, title: opts.title ?? '', index: opts.index, isTemplate: opts.isTemplate, id: opts.id });
      for (const [k, v] of Object.entries(opts.values ?? {})) if (v !== undefined && v !== null) this.setValue(id, k, v);
      if (y && !opts.isTemplate) {
        for (const p of this.schema(dbId))
          if (p.type === 'unique_id') {
            const n = (y.get('nextUniqueId') as number) ?? 1;
            this.ws.setProperty(id, p.id, n);
            y.set('nextUniqueId', n + 1);
          }
      }
      if (opts.isTemplate) (y?.get('templates') as Y.Array<string> | undefined)?.push([id]);
      return id;
    });
  }

  /** Sets a cell. Two-way relations update the other side. */
  setValue(rowId: string, propId: string, value: unknown): void {
    if (propId === TITLE_PROP || propId === '__title') {
      this.ws.setTitle(rowId, String(value ?? ''));
      return;
    }
    const dbId = this.dbOfRow(rowId);
    const prop = dbId ? this.property(dbId, propId) : null;
    this.transact(() => {
      const before = (this.ws.getPage(rowId)?.properties[propId] as string[] | undefined) ?? [];
      this.ws.setProperty(rowId, propId, value as never);
      if (prop?.type === 'relation' && prop.relation?.twoWay && prop.relation.syncedPropId) {
        const after = (value as string[] | null) ?? [];
        const back = prop.relation.syncedPropId;
        // A "one" relation on the other side (a sub-item's parent) moves rather than adds.
        const one = this.property(prop.relation.dbId, back)?.relation?.limit === 'one';
        for (const added of after.filter((x) => !before.includes(x))) {
          const cur = (this.ws.getPage(added)?.properties[back] as string[] | undefined) ?? [];
          if (cur.includes(rowId)) continue;
          if (one) {
            for (const old of cur) {
              const list = (this.ws.getPage(old)?.properties[propId] as string[] | undefined) ?? [];
              if (list.includes(added)) this.ws.setProperty(old, propId, list.filter((x) => x !== added));
            }
          }
          this.ws.setProperty(added, back, one ? [rowId] : [...cur, rowId]);
        }
        for (const removed of before.filter((x) => !after.includes(x))) {
          const cur = (this.ws.getPage(removed)?.properties[back] as string[] | undefined) ?? [];
          if (cur.includes(rowId)) this.ws.setProperty(removed, back, cur.filter((x) => x !== rowId));
        }
      }
    });
  }

  // ───────────── sub-items (P-965) ─────────────

  /** The "Parent item" (one) and "Sub-items" (many) relation of a database to itself, when sub-items are on. */
  subItems(dbId: string): { parent: string; children: string } | null {
    const s = this.db(dbId)?.get('subItems') as { parent: string; children: string } | undefined;
    return s && this.property(dbId, s.parent) && this.property(dbId, s.children) ? s : null;
  }

  enableSubItems(dbId: string): { parent: string; children: string } {
    const cur = this.subItems(dbId);
    if (cur) return cur;
    const s = { parent: newId(), children: newId() };
    this.transact(() => {
      this.addProperty(dbId, { id: s.parent, name: t('Parent item'), type: 'relation', relation: { dbId, twoWay: true, syncedPropId: s.children, limit: 'one' } });
      this.addProperty(dbId, { id: s.children, name: t('Sub-items'), type: 'relation', relation: { dbId, twoWay: true, syncedPropId: s.parent, limit: 'many' } });
      this.db(dbId)!.set('subItems', s);
    });
    return s;
  }

  /** Rows stop nesting; the two relation properties stay. */
  disableSubItems(dbId: string): void {
    this.db(dbId)?.delete('subItems');
  }

  parentOf(rowId: string): string | null {
    const dbId = this.dbOfRow(rowId);
    const s = dbId ? this.subItems(dbId) : null;
    if (!s) return null;
    return ((this.ws.getPage(rowId)?.properties[s.parent] as string[] | undefined) ?? []).find((id) => this.ws.hasPage(id)) ?? null;
  }

  childrenOf(rowId: string): string[] {
    const dbId = this.dbOfRow(rowId);
    const s = dbId ? this.subItems(dbId) : null;
    return s ? ((this.ws.getPage(rowId)?.properties[s.children] as string[] | undefined) ?? []).filter((id) => this.ws.hasPage(id)) : [];
  }

  /** Puts a row under another of its database (null: at the top). Refuses loops; returns whether it was done. */
  setParent(rowId: string, parentId: string | null): boolean {
    const dbId = this.dbOfRow(rowId);
    const s = dbId ? this.subItems(dbId) : null;
    if (!s) return false;
    if (parentId) {
      if (this.dbOfRow(parentId) !== dbId) return false;
      for (let p: string | null = parentId, i = 0; p && i < 10_000; p = this.parentOf(p), i++) if (p === rowId) return false;
    }
    this.setValue(rowId, s.parent, parentId ? [parentId] : []);
    return true;
  }

  addSubItem(parentId: string, opts: { title?: string } = {}): string {
    const dbId = this.dbOfRow(parentId);
    if (!dbId || !this.subItems(dbId)) throw new Error('sub-items are off');
    let id = '';
    this.transact(() => {
      id = this.addRow(dbId, { title: opts.title ?? '' });
      this.setParent(id, parentId);
    });
    return id;
  }

  // ───────────── dependencies (P-966) ─────────────

  /** "Blocked by" and "Blocking": a two-way relation of a database to itself, when dependencies are on. */
  dependencies(dbId: string): { blockedBy: string; blocking: string } | null {
    const d = this.db(dbId)?.get('dependencies') as { blockedBy: string; blocking: string } | undefined;
    return d && this.property(dbId, d.blockedBy) && this.property(dbId, d.blocking) ? d : null;
  }

  enableDependencies(dbId: string): { blockedBy: string; blocking: string } {
    const cur = this.dependencies(dbId);
    if (cur) return cur;
    const d = { blockedBy: newId(), blocking: newId() };
    this.transact(() => {
      this.addProperty(dbId, { id: d.blockedBy, name: t('Blocked by'), type: 'relation', relation: { dbId, twoWay: true, syncedPropId: d.blocking, limit: 'many' } });
      this.addProperty(dbId, { id: d.blocking, name: t('Blocking'), type: 'relation', relation: { dbId, twoWay: true, syncedPropId: d.blockedBy, limit: 'many' } });
      this.db(dbId)!.set('dependencies', d);
    });
    return d;
  }

  /** The timeline stops drawing arrows; the two relation properties stay. */
  disableDependencies(dbId: string): void {
    this.db(dbId)?.delete('dependencies');
  }

  blockersOf(rowId: string): string[] {
    const dbId = this.dbOfRow(rowId);
    const d = dbId ? this.dependencies(dbId) : null;
    return d ? ((this.ws.getPage(rowId)?.properties[d.blockedBy] as string[] | undefined) ?? []).filter((id) => this.ws.hasPage(id)) : [];
  }

  /** `blockerId` blocks `rowId`. Refuses a page blocking itself, or one it (indirectly) waits for. */
  addBlocker(rowId: string, blockerId: string): boolean {
    const dbId = this.dbOfRow(rowId);
    const d = dbId ? this.dependencies(dbId) : null;
    if (!d || rowId === blockerId || this.dbOfRow(blockerId) !== dbId) return false;
    const seen = new Set<string>();
    const waitsFor = (id: string): boolean => {
      if (id === rowId) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return this.blockersOf(id).some(waitsFor);
    };
    if (waitsFor(blockerId)) return false;
    const cur = this.blockersOf(rowId);
    if (!cur.includes(blockerId)) this.setValue(rowId, d.blockedBy, [...cur, blockerId]);
    return true;
  }

  removeBlocker(rowId: string, blockerId: string): void {
    const dbId = this.dbOfRow(rowId);
    const d = dbId ? this.dependencies(dbId) : null;
    if (d) this.setValue(rowId, d.blockedBy, this.blockersOf(rowId).filter((id) => id !== blockerId));
  }

  moveRow(dbId: string, rowId: string, index: number): void {
    this.ws.move(rowId, { type: 'database', id: dbId }, index);
  }

  // ───────────── views ─────────────

  private viewSetIdsOf(dbId: string): string[] {
    const out: string[] = [];
    for (const [id, vs] of this.viewSets.entries()) if (vs.get('source') === dbId) out.push(id);
    return out;
  }

  views(viewSetId: string): ViewDef[] {
    const vs = this.viewSets.get(viewSetId);
    if (!vs) return [];
    const defs = vs.get('viewDefs') as Y.Map<ViewDef>;
    return (vs.get('views') as Y.Array<string>)
      .toArray()
      .map((id) => defs.get(id))
      .filter((v): v is ViewDef => !!v);
  }

  view(viewSetId: string, viewId: string): ViewDef | null {
    return ((this.viewSets.get(viewSetId)?.get('viewDefs') as Y.Map<ViewDef> | undefined)?.get(viewId) as ViewDef | undefined) ?? null;
  }

  addView(viewSetId: string, layout: Layout, name?: string, index?: number): string {
    const vs = this.viewSets.get(viewSetId);
    const src = this.sourceOf(viewSetId);
    if (!vs || !src) throw new Error(`view set ${viewSetId} not found`);
    const v = newView(layout, this.schema(src), name);
    this.transact(() => {
      (vs.get('viewDefs') as Y.Map<ViewDef>).set(v.id, v);
      const arr = vs.get('views') as Y.Array<string>;
      arr.insert(index === undefined ? arr.length : index, [v.id]);
    });
    return v.id;
  }

  updateView(viewSetId: string, viewId: string, patch: Partial<ViewDef>): void {
    const defs = this.viewSets.get(viewSetId)?.get('viewDefs') as Y.Map<ViewDef> | undefined;
    const cur = defs?.get(viewId);
    if (!defs || !cur) return;
    defs.set(viewId, { ...cur, ...patch });
  }

  duplicateView(viewSetId: string, viewId: string): string | null {
    const cur = this.view(viewSetId, viewId);
    const vs = this.viewSets.get(viewSetId);
    if (!cur || !vs) return null;
    const copy: ViewDef = { ...structuredClone(cur), id: newId(), name: `${cur.name} (1)` };
    this.transact(() => {
      (vs.get('viewDefs') as Y.Map<ViewDef>).set(copy.id, copy);
      const arr = vs.get('views') as Y.Array<string>;
      arr.insert(arr.toArray().indexOf(viewId) + 1, [copy.id]);
    });
    return copy.id;
  }

  deleteView(viewSetId: string, viewId: string): void {
    const vs = this.viewSets.get(viewSetId);
    if (!vs) return;
    const arr = vs.get('views') as Y.Array<string>;
    if (arr.length <= 1) return;
    this.transact(() => {
      const i = arr.toArray().indexOf(viewId);
      if (i >= 0) arr.delete(i, 1);
      (vs.get('viewDefs') as Y.Map<ViewDef>).delete(viewId);
    });
  }

  moveView(viewSetId: string, viewId: string, index: number): void {
    const arr = this.viewSets.get(viewSetId)?.get('views') as Y.Array<string> | undefined;
    if (!arr) return;
    this.transact(() => {
      const i = arr.toArray().indexOf(viewId);
      if (i < 0) return;
      arr.delete(i, 1);
      arr.insert(Math.max(0, Math.min(index, arr.length)), [viewId]);
    });
  }

  // ───────────── templates ─────────────

  templates(dbId: string): string[] {
    const arr = this.db(dbId)?.get('templates') as Y.Array<string> | undefined;
    return (arr?.toArray() ?? []).filter((id) => this.ws.getPage(id) && !this.ws.isInTrash(id));
  }

  addTemplate(dbId: string, title = t('New template')): string {
    return this.addRow(dbId, { title, isTemplate: true });
  }

  setRepeat(dbId: string, templateId: string, rule: RepeatRule | null): void {
    const m = this.db(dbId)?.get('repeat') as Y.Map<unknown> | undefined;
    if (!m) return;
    if (rule) m.set(templateId, { rule, lastRun: null });
    else m.delete(templateId);
  }

  repeats(dbId: string): { templateId: string; rule: RepeatRule; lastRun: number | null }[] {
    const m = this.db(dbId)?.get('repeat') as Y.Map<{ rule: RepeatRule; lastRun: number | null }> | undefined;
    return m ? [...m.entries()].map(([templateId, v]) => ({ templateId, ...v })) : [];
  }

  markRepeatRun(dbId: string, templateId: string, at: number): void {
    const m = this.db(dbId)?.get('repeat') as Y.Map<{ rule: RepeatRule; lastRun: number | null }> | undefined;
    const cur = m?.get(templateId);
    if (m && cur) m.set(templateId, { ...cur, lastRun: at });
  }

  // ───────────── value context ─────────────

  valueContext(me: string, now: () => Date = () => new Date()): ValueContext {
    return {
      schemaOf: (dbId) => this.schema(dbId),
      row: (id) => this.rowData(id),
      dbOf: (id) => this.dbOfRow(id),
      memberName: (id) => this.ws.getMember(id)?.name ?? 'Unknown',
      me,
      now,
    };
  }

  allDatabases(): string[] {
    return [...this.dbs.keys()].filter((id) => this.ws.getPage(id) && !this.ws.isInTrash(id));
  }
}
